/**
 * Nakama Match Data Utilities
 * Helper functions for encoding/decoding match data.
 * Supports transparent gzip decompression for messages compressed by the server.
 */

/**
 * Flag bit ORed into the opCode by the server when the payload is gzip-compressed.
 * Must stay in sync with compressionFlagBit in game-server/modules/shared/broadcast.go.
 * All real game opcodes are < 1000, so bit 30 (0x40000000) never collides.
 */
export const COMPRESSION_FLAG_BIT = 0x40000000;

/**
 * Decompress a gzip-compressed Uint8Array using the browser's native DecompressionStream API.
 * Returns the decompressed bytes as a Uint8Array.
 */
export async function decompressGzip(compressed: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('gzip');
  const writer = ds.writable.getWriter();
  const reader = ds.readable.getReader();

  writer.write(compressed as Uint8Array<ArrayBuffer>);
  writer.close();

  const chunks: Uint8Array[] = [];
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }

  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}

/**
 * Decode match data from Nakama format to JavaScript object.
 * Nakama sends match data as Uint8Array/ArrayBuffer (bytes); decode to string then parse JSON.
 *
 * @param data - Raw match data from Nakama (string, Uint8Array, or ArrayBuffer)
 * @returns Parsed JavaScript object
 */
export function decodeMatchData<T = unknown>(data: string | Uint8Array | ArrayBuffer): T {
  let dataString: string;

  if (typeof data === 'string') {
    dataString = data;
  } else if (data instanceof Uint8Array) {
    const decoder = new TextDecoder('utf-8');
    dataString = decoder.decode(data);
  } else if (data instanceof ArrayBuffer) {
    const decoder = new TextDecoder('utf-8');
    dataString = decoder.decode(new Uint8Array(data));
  } else {
    throw new Error(`[Nakama] Unknown match data type: ${typeof data}`);
  }

  try {
    return JSON.parse(dataString);
  } catch (error) {
    console.error('[Nakama] Failed to parse match data JSON:', error);
    console.error('[Nakama] Data string:', dataString);
    throw error;
  }
}
